import React, { useState } from "react";
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Textarea,
} from "@fluentui/react-components";
import {
  TableCell,
  TableCellLayout,
  TableRow,
  TableRowData as RowStateBase,
} from "@fluentui/react-components/unstable";
import { MoreHorizontalRegular } from "@fluentui/react-icons";
import { ListChildComponentProps } from "react-window";
import { AppConfirmdialog } from "../common";
import { IResourceAllocationSummaryRowApi } from "../../services/types/resourceAllocation";
import dataTimeService from "../../services/dataTime.service";
import { getContractTypeLabel } from "../../utils/contractType";
import {
  resolveDecisionBadgePresentation,
  resolveResourceAllocationDecision,
} from "./resourceAllocationRowPresentation";
import styles from "./ExpectedHoursRowStyles";

interface ReactWindowRenderFnProps extends ListChildComponentProps {
  data: TableRowData[];
  acceptSettlement: (row: IResourceAllocationSummaryRowApi) => Promise<void>;
  rejectSettlement: (row: IResourceAllocationSummaryRowApi, rejectionReason: string) => Promise<void>;
  onRowClick: (row: IResourceAllocationSummaryRowApi) => void;
}

interface TableRowData extends RowStateBase<IResourceAllocationSummaryRowApi> {}

const ResourceAllocationSummaryRow: React.FC<ReactWindowRenderFnProps> = ({
  data,
  index,
  style,
  acceptSettlement,
  rejectSettlement,
  onRowClick,
}) => {
  const [openAcceptDialog, setOpenAcceptDialog] = useState(false);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectValidationError, setRejectValidationError] = useState("");

  const row = data[index];
  const item = row.item;
  const decision = resolveResourceAllocationDecision(
    item.settlementStatus,
    item.hasResourceAllocationOrders
  );
  const badgePresentation = resolveDecisionBadgePresentation(decision);

  const resetRejectDialogState = () => {
    setRejectionReason("");
    setRejectValidationError("");
  };

  const handleAccept = async () => {
    setIsSubmitting(true);
    try {
      await acceptSettlement(item);
      setOpenAcceptDialog(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    const trimmedReason = rejectionReason.trim();
    if (!trimmedReason) {
      setRejectValidationError("Powód odrzucenia jest wymagany.");
      return;
    }

    setRejectValidationError("");
    setIsSubmitting(true);
    try {
      await rejectSettlement(item, trimmedReason);
      setOpenRejectDialog(false);
      resetRejectDialogState();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <TableRow
        style={style}
        aria-rowindex={index + 2}
        onClick={() => onRowClick(item)}
        key={item.employeeId}
      >
        <TableCell>
          <TableCellLayout>{`${item.lastName} ${item.firstName}`}</TableCellLayout>
        </TableCell>
        <TableCell>
          <TableCellLayout>{getContractTypeLabel(item.contractType)}</TableCellLayout>
        </TableCell>
        <TableCell>
          <TableCellLayout>{dataTimeService.convertFormat(item.orderedMinutes)}</TableCellLayout>
        </TableCell>
        <TableCell>
          <TableCellLayout>
            {dataTimeService.convertFormat(item.loggedMinutes ?? 0)}
          </TableCellLayout>
        </TableCell>
        <TableCell style={styles.statusCell}>
          <TableCellLayout style={styles.statusCellLayout}>
            {badgePresentation ? (
              <Badge
                shape="rounded"
                size="medium"
                appearance={badgePresentation.appearance}
                color={badgePresentation.color}
                style={styles.decisionBadge}
              >
                {badgePresentation.label}
              </Badge>
            ) : (
              "—"
            )}
          </TableCellLayout>
        </TableCell>
        <TableCell style={styles.actionsCell} onClick={(e) => e.stopPropagation()}>
          <TableCellLayout style={styles.actionsCellLayout}>
            {item.canManageSettlement ? (
              <Menu>
                <MenuTrigger disableButtonEnhancement>
                  <Button
                    size="small"
                    appearance="outline"
                    icon={<MoreHorizontalRegular />}
                    disabled={isSubmitting}
                    aria-label="Akcje"
                  />
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    <MenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenAcceptDialog(true);
                      }}
                    >
                      Zaakceptuj
                    </MenuItem>
                    <MenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        resetRejectDialogState();
                        setOpenRejectDialog(true);
                      }}
                    >
                      Odrzuć
                    </MenuItem>
                  </MenuList>
                </MenuPopover>
              </Menu>
            ) : null}
          </TableCellLayout>
        </TableCell>
      </TableRow>

      <AppConfirmdialog
        title="Potwierdź akceptację"
        bodyTxt="Czy na pewno chcesz zaakceptować rozliczenie godzin pracy tego pracownika?"
        setOpen={setOpenAcceptDialog}
        open={openAcceptDialog}
        onAccept={handleAccept}
        isLoading={isSubmitting}
      />

      <Dialog
        open={openRejectDialog}
        onOpenChange={(_, data) => {
          if (!data.open) {
            setOpenRejectDialog(false);
            resetRejectDialogState();
          }
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Odrzuć rozliczenie</DialogTitle>
            <DialogContent>
              <Textarea
                placeholder="Powód odrzucenia"
                value={rejectionReason}
                onChange={(_, data) => setRejectionReason(data.value)}
                resize="vertical"
              />
              {rejectValidationError ? (
                <span style={{ color: "red" }}>{rejectValidationError}</span>
              ) : null}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setOpenRejectDialog(false)}>
                Anuluj
              </Button>
              <Button appearance="primary" onClick={handleReject} disabled={isSubmitting}>
                Odrzuć
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
};

export default ResourceAllocationSummaryRow;
